import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, DOMAIN_CONFIG } from './docRectifConstants';

export function useDocRectif({ open, onClose, reference, getAuthHeader, defaultDocumentType, domain = 'domium' }) {
  const cfg = DOMAIN_CONFIG[domain] || DOMAIN_CONFIG.domium;
  const docTypesList = Object.entries(cfg.docTypes);
  const fallbackDocType = defaultDocumentType || docTypesList[0][0];

  const [mode, setMode] = useState('edit');
  const [documentType, setDocumentType] = useState(fallbackDocType);
  const [motif, setMotif] = useState('');
  const [edits, setEdits] = useState({});
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [archives, setArchives] = useState([]);
  // V37.87 — Valeurs actuelles du dossier (dotted.path -> valeur), affichées
  // en placeholder dans chaque champ pour montrer ce qui sera modifié.
  const [currentValues, setCurrentValues] = useState({});
  const [lastResult, setLastResult] = useState(null);
  const [fieldSearch, setFieldSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  // V36.40 — Chips de filtre rapide pour les ~189 champs editables.
  // Reduit drastiquement le scroll et le bruit visuel pendant la rectification.
  const [quickFilter, setQuickFilter] = useState('all');
  // --- Preview split-view state (universel DOMIUM + RCDS) ------------------
  // Types previsualisables selon le domaine.
  const PREVIEW_DOC_TYPES = domain === 'rcds'
    ? ['rcds_pack_unique', 'rcds_attestation']
    : ['attestation_definitive', 'cp', 'cg', 'attestation_provisoire', 'attestation_nominative'];
  const defaultPreviewType = PREVIEW_DOC_TYPES[0];
  const [showSplitView, setShowSplitView] = useState(false);
  const [previewDocType, setPreviewDocType] = useState(defaultPreviewType);
  const [originalPdfUrl, setOriginalPdfUrl] = useState(null);
  const [originalLoading, setOriginalLoading] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Endpoints adaptes selon le domaine (DOMIUM : /domium/admin/documents,
  // RCDS : /rcds-dashboard/admin/souscriptions).
  const PREVIEW_BASE_URL = domain === 'rcds'
    ? `${API}/api/rcds-dashboard/admin/souscriptions/${reference}`
    : `${API}/api/domium/admin/documents/${reference}`;

  // Normalisation : on accepte fieldGroups (sectionne) OU editableFields (flat).
  const fieldGroups = cfg.fieldGroups
    ? cfg.fieldGroups
    : [{ title: 'Champs editables', fields: cfg.editableFields || [] }];
  const totalFields = fieldGroups.reduce((acc, g) => acc + g.fields.length, 0);

  useEffect(() => {
    if (!open || !reference) return;
    setMode('edit');
    setDocumentType(fallbackDocType);
    setMotif('');
    setEdits({});
    setFile(null);
    setLastResult(null);
    // Charger les archives existantes (axios pour eviter clone-bug en prod)
    (async () => {
      try {
        const res = await axios.get(`${API}${cfg.archivesUrl(reference)}`, {
          headers: { ...getAuthHeader() },
          validateStatus: () => true,
        });
        if (res.status >= 200 && res.status < 300 && res.data?.archives) {
          setArchives(res.data.archives);
        }
      } catch { /* non bloquant */ }
    })();
    // V37.87 — Charger les valeurs actuelles (placeholder des champs).
    {
      (async () => {
        try {
          const res = await axios.get(
            domain === 'rcds'
              ? `${API}/api/rcds-dashboard/admin/souscriptions/${reference}/current-values`
              : `${API}/api/domium/admin/documents/${reference}/current-values`,
            { headers: { ...getAuthHeader() }, validateStatus: () => true },
          );
          if (res.status >= 200 && res.status < 300 && res.data?.values) {
            setCurrentValues(res.data.values);
          }
        } catch { setCurrentValues({}); }
      })();
    }
  }, [open, reference, fallbackDocType, getAuthHeader, cfg, domain]);

  // V37.86b — Synchronise l'aperçu split-view avec le type de document choisi
  // (ex : « Attestation Nominative » → l'aperçu montre directement ce PDF).
  // V37.89 — Active aussi le filtre rapide « Attestation nominative » pour
  // n'afficher QUE les champs présents dans ce document.
  useEffect(() => {
    if (PREVIEW_DOC_TYPES.includes(documentType)) setPreviewDocType(documentType);
    if (documentType === 'attestation_nominative') {
      setQuickFilter('nominative');
    } else {
      setQuickFilter((prev) => (prev === 'nominative' ? 'all' : prev));
    }
  }, [documentType]);

  const handleEditField = (key, value) => {
    setEdits((prev) => {
      const next = { ...prev };
      if (value === '' || value === null || value === undefined) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  // ---- Split-view PDF preview (universel domium + rcds) -----------------
  // Note : on utilise axios (XHR) plutot que fetch pour ces telechargements
  // PDF afin d'eviter le bug "Failed to execute 'clone' on Response" rencontre
  // en production avec le Service Worker / extensions navigateur. axios avec
  // responseType:'blob' contourne entierement la pile fetch/SW.
  const fetchOriginalPdf = useCallback(async (docType) => {
    if (!reference) return;
    setPreviewError(null);
    // V37.88 — vider l'ancien PDF immédiatement (évite d'afficher un document
    // obsolète pendant la génération du nouveau type) + spinner.
    setOriginalPdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setOriginalLoading(true);
    try {
      const res = await axios.get(
        `${PREVIEW_BASE_URL}/original-pdf`,
        {
          params: { document_type: docType, _ts: Date.now() },
          headers: { ...getAuthHeader() },
          responseType: 'blob',
          validateStatus: () => true,
        },
      );
      if (res.status < 200 || res.status >= 300) {
        let msg = `HTTP ${res.status}`;
        try {
          const txt = await res.data.text();
          const parsed = JSON.parse(txt);
          msg = parsed.detail || parsed.error || msg;
        } catch { /* binary or non-JSON */ }
        throw new Error(msg);
      }
      const url = URL.createObjectURL(res.data);
      setOriginalPdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (e) {
      setOriginalPdfUrl(null);
      setPreviewError(`Original indisponible : ${e.message || e}`);
    } finally {
      setOriginalLoading(false);
    }
  }, [reference, getAuthHeader, PREVIEW_BASE_URL]);

  const fetchPreviewPdf = useCallback(async () => {
    if (!reference) return;
    if (Object.keys(edits).length === 0) {
      setPreviewError('Aucun champ modifie — saisissez au moins une valeur pour voir l\'apercu.');
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await axios.post(
        `${PREVIEW_BASE_URL}/preview-rectified`,
        { document_type: previewDocType, edits },
        {
          headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
          responseType: 'blob',
          validateStatus: () => true,
        },
      );
      if (res.status < 200 || res.status >= 300) {
        let msg = `HTTP ${res.status}`;
        try {
          const txt = await res.data.text();
          const parsed = JSON.parse(txt);
          msg = parsed.detail || parsed.error || msg;
        } catch { /* not JSON */ }
        throw new Error(msg);
      }
      const url = URL.createObjectURL(res.data);
      setPreviewPdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      toast.success('Apercu actualise');
    } catch (e) {
      setPreviewError(e.message || String(e));
      toast.error(`Echec apercu : ${e.message || e}`);
    } finally {
      setPreviewLoading(false);
    }
  }, [reference, edits, previewDocType, getAuthHeader, PREVIEW_BASE_URL]);

  // Quand on bascule en split-view ou change le doc previsualise, recharger l'original
  useEffect(() => {
    if (showSplitView && previewDocType) {
      fetchOriginalPdf(previewDocType);
      // Reset previous preview (force re-fetch via bouton)
      setPreviewPdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    }
  }, [showSplitView, previewDocType, fetchOriginalPdf]);

  // Cleanup blob URLs on close
  useEffect(() => {
    if (!open) {
      setOriginalPdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      setPreviewPdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      setShowSplitView(false);
      setPreviewError(null);
    }
  }, [open]);
  // --- Auto-redaction du motif (DOMIUM) ------------------------------------
  // L'utilisateur choisit "A la demande de ..." dans un select. Le motif est
  // pre-compose automatiquement avec la liste des champs modifies. Il reste
  // entierement editable manuellement.
  const REQUESTER_OPTIONS = [
    { value: 'souscripteur', label: 'du souscripteur' },
    { value: 'banque', label: "de la banque" },
    { value: 'notaire', label: 'du notaire' },
    { value: 'mo', label: "du Maitre d'Ouvrage" },
    { value: 'moe', label: "du Maitre d'Oeuvre / architecte" },
    { value: 'controleur_technique', label: 'du Controleur Technique' },
    { value: 'assureur', label: "de l'assureur" },
    { value: 'expert', label: "de l'expert" },
    { value: 'admin_interne', label: 'correction administrative interne' },
    { value: 'autre', label: 'autre — preciser dans le motif' },
  ];
  const [requester, setRequester] = useState('souscripteur');
  const [autoMotif, setAutoMotif] = useState(true);

  // Reconstruit la phrase a partir des edits actuels + requester
  const buildAutoMotif = useCallback(() => {
    const keys = Object.keys(edits);
    if (keys.length === 0) return '';
    const labelByKey = new Map();
    fieldGroups.forEach((g) => g.fields.forEach((f) => labelByKey.set(f.key, f.label)));
    const labels = keys.map((k) => labelByKey.get(k) || k);
    const reqLabel = REQUESTER_OPTIONS.find((r) => r.value === requester)?.label || requester;
    const today = new Date().toLocaleDateString('fr-FR');
    const fieldsPart = labels.length <= 3
      ? labels.join(', ')
      : `${labels.slice(0, 3).join(', ')} (+${labels.length - 3} autre(s))`;
    return `A la demande ${reqLabel} (le ${today}) : correction de ${labels.length} champ(s) — ${fieldsPart}.`;
  }, [edits, requester, fieldGroups]);

  // Synchro automatique du motif lorsque autoMotif=true
  useEffect(() => {
    if (autoMotif) {
      const next = buildAutoMotif();
      // Ne synchronise que si > 0 edits (sinon on garde un motif vide pour
      // l'indicateur d'erreur "saisissez un motif").
      if (next) setMotif(next);
    }
  }, [edits, requester, autoMotif, buildAutoMotif]);
  // ------------------------------------------------------------------------

  const canSubmit = () => {
    if (motif.trim().length < 10) return false;
    if (mode === 'edit') return Object.keys(edits).length > 0;
    if (mode === 'upload') return !!file;
    return false;
  };

  // Raison precise pour laquelle le bouton est desactive (affichee en tooltip
  // et dans une bannerre rouge sous le bouton Rectifier — evite la confusion).
  const submitBlockedReason = () => {
    const reasons = [];
    if (motif.trim().length === 0) reasons.push('saisissez un motif');
    else if (motif.trim().length < 10) reasons.push(`completez le motif (${motif.trim().length}/10 caracteres)`);
    if (mode === 'edit' && Object.keys(edits).length === 0) reasons.push('modifiez au moins un champ');
    if (mode === 'upload' && !file) reasons.push('chargez un PDF correctif');
    return reasons.length > 0 ? reasons.join(' + ') : null;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('document_type', documentType);
      fd.append('motif', motif.trim());
      fd.append('mode', mode);
      if (mode === 'edit') fd.append('edits', JSON.stringify(edits));
      if (mode === 'upload' && file) fd.append('file', file);

      // Utilisation d'axios (XHR) au lieu de fetch pour eviter le bug
      // "Failed to execute 'clone' on 'Response'" rencontre en production
      // (interaction avec Service Worker ou extensions navigateur).
      const res = await axios.post(
        `${API}${cfg.rectifyUrl(reference)}`,
        fd,
        {
          headers: { ...getAuthHeader() },
          // Laisser axios gerer le Content-Type (multipart boundary)
          validateStatus: () => true,
        },
      );
      const data = res.data;
      if (res.status < 200 || res.status >= 300 || !data || data.success === false) {
        const msg = (data && (data.detail || data.error))
          || (typeof data === 'string' ? data.slice(0, 300) : null)
          || `HTTP ${res.status}`;
        throw new Error(msg);
      }
      setLastResult(data);
      toast.success(`Document ${documentType} rectifie - version originale archivee (v${data.archive?.version || '?'})`);
      // Reload archives (axios)
      try {
        const r2 = await axios.get(`${API}${cfg.archivesUrl(reference)}`, {
          headers: { ...getAuthHeader() },
          validateStatus: () => true,
        });
        if (r2.status >= 200 && r2.status < 300 && r2.data?.archives) {
          setArchives(r2.data.archives);
        }
      } catch { /* */ }
    } catch (e) {
      toast.error(`Echec rectification : ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };


  return { open, onClose, reference, getAuthHeader, defaultDocumentType, domain, cfg, docTypesList, fallbackDocType, mode, setMode, documentType, setDocumentType, motif, setMotif, edits, setEdits, file, setFile, submitting, setSubmitting, archives, setArchives, currentValues, setCurrentValues, lastResult, setLastResult, fieldSearch, setFieldSearch, collapsedGroups, setCollapsedGroups, quickFilter, setQuickFilter, PREVIEW_DOC_TYPES, defaultPreviewType, showSplitView, setShowSplitView, previewDocType, setPreviewDocType, originalPdfUrl, setOriginalPdfUrl, originalLoading, setOriginalLoading, previewPdfUrl, setPreviewPdfUrl, previewLoading, setPreviewLoading, previewError, setPreviewError, PREVIEW_BASE_URL, fieldGroups, totalFields, handleEditField, fetchOriginalPdf, fetchPreviewPdf, REQUESTER_OPTIONS, requester, setRequester, autoMotif, setAutoMotif, buildAutoMotif, canSubmit, submitBlockedReason, handleSubmit };
}
